import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Cable, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProjectSelectionDialog } from '@/components/ProjectSelectionDialog';
import { ProjectNameDialog } from '@/components/ProjectNameDialog';
import { useProjects } from '@/hooks/useProjects';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useCameras } from '@/hooks/useCameras';
import { useAccessoriesWithRules } from '@/hooks/useAccessoriesWithRules';
import { toast } from '@/lib/toast';
import { supabase } from '@/integrations/supabase/client';
import type { SaveRigData } from '@/hooks/useRigAssistant';
import type { Project } from '@/types/project';
import type { CableConnection } from '@/types/cableRouting';
import type { Json } from '@/integrations/supabase/types';

interface ChatSaveRigCardProps {
  saveData: SaveRigData;
  onSaved: () => void;
  onDismiss: () => void;
}

export function ChatSaveRigCard({ saveData, onSaved, onDismiss }: ChatSaveRigCardProps) {
  const navigate = useNavigate();
  const { projects, createProject, addRig, refreshProjects } = useProjects();
  const { canCreateProject } = usePlanLimits();
  const { cameras } = useCameras();
  const { accessories } = useAccessoriesWithRules();
  
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedRig, setSavedRig] = useState<{ projectId: string; rigId: string } | null>(null);

  const handleSaveClick = () => {
    setShowProjectDialog(true);
  };

  const handleProjectSelect = async (project: Project) => {
    setShowProjectDialog(false);
    await saveRigToProject(project.id);
  };

  const handleCreateProject = () => {
    setShowProjectDialog(false);
    setShowNameDialog(true);
  };

  const handleProjectNameSubmit = async (name: string) => {
    setShowNameDialog(false);
    setIsSaving(true);
    
    try {
      const result = await createProject(name);
      if (result.success && result.project) {
        await saveRigToProject(result.project.id);
      } else {
        toast.error('Failed to create project. You may have reached your project limit.');
        setIsSaving(false);
      }
    } catch (err) {
      console.error('Failed to create project:', err);
      toast.error('Failed to create project');
      setIsSaving(false);
    }
  };

  const saveRigToProject = async (projectId: string) => {
    setIsSaving(true);
    
    try {
      
      // Find the camera from the database
      const camera = cameras.find(c => c.id === saveData.cameraId);
      if (!camera) {
        console.error('Camera not found. Looking for:', saveData.cameraId);
        toast.error('Camera not found in database');
        setIsSaving(false);
        return;
      }

      // Find the accessories from the database
      const selectedAccessories = saveData.accessoryIds
        .map(id => {
          const found = accessories.find(a => a.id === id);
          if (!found) console.warn('Accessory not found:', id);
          return found;
        })
        .filter(Boolean) as any[];

      const rigStyleFormatted = saveData.rigStyle.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const rigName = `AI-Built ${camera.name} ${rigStyleFormatted} Rig`;
      
      const result = await addRig({
        name: rigName,
        camera: camera,
        rigStyle: saveData.rigStyle as any,
        accessories: selectedAccessories,
        autoAddedCables: {},
      }, projectId);

      if (result.success && result.rig) {
        // If AI provided connections, save them as cable routing
        if (saveData.connections?.length) {
          const cableConnections: CableConnection[] = saveData.connections.map((conn, i) => ({
            id: `ai-conn-${Date.now()}-${i}`,
            connectionType: conn.connectionType,
            isDirect: conn.isDirect,
            source: {
              deviceId: conn.sourceDeviceId,
              deviceType: conn.sourceDeviceId === saveData.cameraId ? 'camera' as const : 'accessory' as const,
              portType: conn.sourcePortType,
            },
            target: {
              deviceId: conn.targetDeviceId,
              deviceType: conn.targetDeviceId === saveData.cameraId ? 'camera' as const : 'accessory' as const,
              portType: conn.targetPortType,
            },
          }));
          
          // Update rig with cable routing (cast to Json for Supabase compatibility)
          const cableRouting = { connections: cableConnections } as unknown as Json;
          await supabase
            .from('rigs')
            .update({ cable_routing: cableRouting })
            .eq('id', result.rig.id);
          
          console.log('AI-generated connections saved:', cableConnections.length);
        }
        
        setSavedRig({ projectId, rigId: result.rig.id });
        toast.success('Rig saved successfully!');
        onSaved();
        await refreshProjects();
      } else {
        toast.error('Failed to save rig. You may have reached your rig limit.');
      }
    } catch (err) {
      console.error('Failed to save rig:', err);
      toast.error('Failed to save rig');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfigureCables = () => {
    if (savedRig) {
      navigate(`/rig/${savedRig.projectId}/${savedRig.rigId}`);
    }
  };

  // Already saved state
  if (savedRig) {
    return (
      <div className="mt-3 p-3 rounded-xl bg-green-500/10 border border-green-500/30">
        <div className="flex items-center gap-2 text-green-400 mb-3">
          <Check className="w-4 h-4" />
          <span className="font-medium text-sm">Rig saved!</span>
        </div>
        <Button
          onClick={handleConfigureCables}
          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
        >
          <Cable className="w-4 h-4 mr-2" />
          Configure Cables
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="mt-3 p-3 rounded-xl bg-purple-500/10 border border-purple-500/30">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-zinc-300">Ready to save this rig?</span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              disabled={isSaving}
              className="text-zinc-400 hover:text-zinc-200"
            >
              Dismiss
            </Button>
            <Button
              size="sm"
              onClick={handleSaveClick}
              disabled={isSaving}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Rig
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <ProjectSelectionDialog
        open={showProjectDialog}
        onOpenChange={setShowProjectDialog}
        projects={projects}
        onSelectProject={handleProjectSelect}
        onCreateNew={handleCreateProject}
        canCreateProject={canCreateProject}
        onLimitReached={() => {
          toast.error('You have reached your project limit. Upgrade to create more projects.');
        }}
      />

      <ProjectNameDialog
        open={showNameDialog}
        onOpenChange={setShowNameDialog}
        onSubmit={handleProjectNameSubmit}
        title="Create New Project"
        submitLabel="Create & Save Rig"
      />
    </>
  );
}